import type { ReactElement } from 'react'
import { useT } from 'flyt/lib/language/language'
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  InfoIcon,
  XIcon,
} from 'flyt/ui/Icons/Icons'
import type { AlertProps, AlertStatus } from './Alert.types'

/**
 * alert — the portal's one way of stating that something happened.
 *
 * ONE SEVERITY, ONE APPEARANCE, EVERYWHERE. A saved change, a failed upload
 * and a planned outage are the same kind of statement at three severities, so
 * they are the same component wearing three tints — not three bespoke boxes
 * that happen to look similar until someone edits one of them.
 *
 * THE WORDS CARRY THE FACT, THE COLOUR ONLY REPEATS IT (theme design rule 5).
 * The glyph and the tint are a second channel for a reader who already read
 * the sentence; neither is allowed to be the only place the severity is
 * stated, which is why the glyph carries the severity word for a screen
 * reader and why an error message must still SAY what went wrong.
 *
 * WHO ANNOUNCES IT FOLLOWS THE PRESENTATION. An `inline` alert appears in the
 * page next to what it is about and is the only thing that can announce
 * itself, so it carries the live role. A `raised` one was floated over the
 * page by a host that already runs a live region; a second role nested inside
 * that one makes a screen reader say everything twice.
 */

/** Each severity's glyph. The shape says the severity before the colour does. */
const STATUS_ICON: Readonly<Record<AlertStatus, typeof InfoIcon>> = {
  info: InfoIcon,
  success: CheckCircleIcon,
  warning: AlertTriangleIcon,
  error: AlertCircleIcon,
}

/**
 * Each severity's paint: the tint background paired with its AA-safe ink, the
 * documented `bg-{status}-surface text-{status}-strong` reading. `error` is
 * painted from the `danger` trio.
 */
const STATUS_PAINT: Readonly<Record<AlertStatus, string>> = {
  info: 'border-info bg-info-surface text-info-strong',
  success: 'border-success bg-success-surface text-success-strong',
  warning: 'border-warning bg-warning-surface text-warning-strong',
  error: 'border-danger bg-danger-surface text-danger-strong',
}

/**
 * The two shapes. `raised` is capped rather than fluid: a floating message
 * stretched to the viewport is a banner, and a line of text 1200px wide is a
 * line nobody finishes.
 */
const PRESENTATION_SHAPE: Readonly<Record<'inline' | 'raised', string>> = {
  inline: 'w-full',
  raised: 'w-[min(26rem,calc(100vw-2rem))] shadow-overlay',
}

export function Alert({
  status,
  title,
  children,
  onClose,
  presentation = 'inline',
}: AlertProps): ReactElement {
  const t = useT('common')
  const Icon = STATUS_ICON[status]
  return (
    <div
      role={presentation === 'inline' ? 'alert' : undefined}
      className={`flex items-start gap-3 rounded-lg border p-4 ${STATUS_PAINT[status]} ${PRESENTATION_SHAPE[presentation]}`}
    >
      {/* The severity word rides WITH the glyph rather than in a corner of its
          own, so a screen reader reads "Feil" immediately before the sentence
          it qualifies — the same order the eye takes it in. */}
      <span className="shrink-0 pt-0.5">
        <Icon width={18} height={18} />
        <span className="sr-only">{t(`alert.status.${status}`)}</span>
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title === undefined ? null : <p className="text-sm font-semibold">{title}</p>}
        <div className="text-sm">{children}</div>
      </div>
      {onClose === undefined ? null : (
        <button
          type="button"
          onClick={onClose}
          // ⚠️ THE CLOSE CONTROL OWNS ITS OWN POINTER. A raised alert floats
          // inside a host that reads drags across the message to swipe it away;
          // pressing the X starts such a drag and turns the press into a swipe
          // too short to dismiss anything. Stopping the press here means the
          // host never sees it, and the rest of the message still swipes.
          onPointerDown={(event) => {
            event.stopPropagation()
          }}
          aria-label={t('actions.close')}
          // The glyph never answers the pointer: a press must land on the
          // BUTTON, not on a path inside it — the host above reads the press
          // target to decide whether it was a button at all.
          /* IT ALIGNS TO THE FIRST LINE, NOT TO THE BOX. The row is top-aligned
             so a long message keeps its glyph beside its opening words — but
             the close control is a 36px target wrapped around an 18px glyph,
             and hanging that box from the top left the X sitting 4px BELOW the
             text it belongs to. The pull is half the
             difference between the target and the line it must centre on
             ((36-20)/2 = 8px), so the glyph lands on the text's own centre and
             the target keeps its full size. */
          className="-my-2 -mr-1 flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md outline-offset-2 hover:bg-text/8 focus-visible:outline-2 focus-visible:outline-current [&_svg]:pointer-events-none"
        >
          <XIcon width={18} height={18} />
        </button>
      )}
    </div>
  )
}
